import React, { type PropsWithChildren, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { useLocation, useParams } from "react-router";
import {
  type GetSubmissionTypeForBuilderQuery,
  useGetSubmissionTypeForBuilderQuery,
} from "../../../../../generated/graphql";
import FullPageFormLayout from "../../../../Common/FullPageFormLayout";
import FullPageLoadingScreen from "../../../../Common/FullPageLoadingScreen";
import { SubmissionsBuilderWrapper } from "./__styles__/SubmissionsBuilder";
import { useShowCreateSubmissionTypeModalOnEnter } from "./CreateSubmissionTypeModal";
import {
  getDefaultValues,
  useBuilderForm,
  useBuilderFormContext,
} from "./form";
import { HeaderMiddleContainer } from "./HeaderMiddleContainer";
import { MainColumn } from "./MainColumn/MainColumn";
import { MenuButtons } from "./MenuButtons";
import { SidebarColumn } from "./SidebarColumn/SidebarColumn";
import {
  SUBMISSIONS_BUILDER_MODE,
  SubmissionsBuilderContextProvider,
  useSubmissionsBuilderMode,
} from "./SubmissionsBuilderContext";
import { VersionHistory } from "./VersionHistory/VersionHistory";

type SubmissionsBuilderProps = {
  existingSubmissionType?: GetSubmissionTypeForBuilderQuery["submissionType"];
};

function SubmissionsBuilderInner({
  existingSubmissionType,
}: SubmissionsBuilderProps) {
  const defaultValues = useMemo(() => {
    return getDefaultValues({
      existingSubmissionType,
      // TODO: [SUBMISSIONS] this should use the selected value from context eventually
      versionId: existingSubmissionType?.currentVersion?.id,
    });
  }, [existingSubmissionType]);

  const builderFormMethods = useBuilderForm({
    mode: "onChange",
    defaultValues,
  });

  const { setValue, getValues } = builderFormMethods;

  useShowCreateSubmissionTypeModalOnEnter({
    existingSubmissionType,
    setValue,
    getValues,
  });

  const [mode] = useSubmissionsBuilderMode();
  const isVersionHistoryMode =
    mode === SUBMISSIONS_BUILDER_MODE.VERSION_HISTORY;

  return (
    <FormProvider {...builderFormMethods}>
      <FormLayout>
        <SubmissionsBuilderWrapper hasRightSidebar={isVersionHistoryMode}>
          <SidebarColumn />
          <MainColumn />
          {isVersionHistoryMode && <VersionHistory />}
        </SubmissionsBuilderWrapper>
      </FormLayout>
    </FormProvider>
  );
}

function FormLayout({ children }: PropsWithChildren<{}>) {
  const location = useLocation<undefined | { prevLocation?: string }>();
  const prevLocation =
    location.state?.prevLocation ?? "/settings/account/records";

  const { watch } = useBuilderFormContext();
  const formName = watch("name");
  const subtitle = formName ? `${formName} form` : "Add record type";

  return (
    <FullPageFormLayout
      subtitle={subtitle}
      prevLocation={prevLocation}
      middleContainer={<HeaderMiddleContainer />}
      rightContainer={<MenuButtons />}
      centered={false}
      width="auto"
    >
      {children}
    </FullPageFormLayout>
  );
}

export function SubmissionsBuilderV2() {
  const { submissionTypeId } = useParams<{ submissionTypeId?: string }>();

  const { data, error, loading } = useGetSubmissionTypeForBuilderQuery({
    fetchPolicy: "cache-and-network",
    variables: { submissionTypeId: submissionTypeId! },
    skip: !submissionTypeId,
  });

  if (error) {
    return <em>There was a problem loading your submission</em>;
  }

  if (loading) {
    return <FullPageLoadingScreen />;
  }

  return (
    <SubmissionsBuilderContextProvider
      existingSubmissionType={data?.submissionType}
    >
      <SubmissionsBuilderInner existingSubmissionType={data?.submissionType} />
    </SubmissionsBuilderContextProvider>
  );
}
